import React, { FC, useState } from 'react';
import { LayoutVariant } from '../../Core/CommonTypes';
import OrderInfo from '../SubmitOnlineOrder/OrderInfo/OrderInfo';
import { useOrdering } from '../../../stores/OrderingStore';
import {
  makeStyles,
  Dialog,
  DialogActions,
  Button,
  DialogTitle,
  Typography,
  IconButton,
  DialogContent
} from '@material-ui/core';
import { OnlineOrderingStore } from 'kiolyn-api';
import { OnlineOrderType } from '../../../domain/models/CommonTypes';
import isEmpty from 'lodash/isEmpty';
import { Close } from '@material-ui/icons';
import { useObserver } from 'mobx-react-lite';
import { ValidationErrors } from '../../Core/Validators';
import { ViewState } from '../../../domain/models/ViewState';

const useStyles = makeStyles(theme => ({
  paper: {
    height: 'calc(100% - 96px)',
    minWidth: 480
  },
  title: {
    marginRight: 48
  },
  close: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1)
  },
  dialogContent: {},
  mobileDialogContent: {
    padding: theme.spacing(2),
    paddingTop: 0
  }
}));

interface OrderInfoLandingPageProps {
  onClose?: () => void;
  variant?: LayoutVariant;
}

const OrderInfoLandingPage: FC<OrderInfoLandingPageProps> = ({
  onClose,
  variant
}) => {
  const classes = useStyles();
  const orderingStore = useOrdering();
  const order = orderingStore.order;
  const store = orderingStore.store as OnlineOrderingStore;

  const acceptOrderTypes: OnlineOrderType[] = [];
  if (store.accept_delivery === true) {
    acceptOrderTypes.push('delivery');
  }
  if (store.accept_pickup === true) {
    acceptOrderTypes.push('pickup');
  }
  if (store.accept_dinein === true) {
    acceptOrderTypes.push('dinein');
  }
  if (!acceptOrderTypes.find(t => `${t}` === order.order_type)) {
    orderingStore.setOrderType(acceptOrderTypes[0]);
  }

  const [havingError, setHavingError] = useState(true);

  const handleUpdateCalculatedValues = async () => {
    await orderingStore.updateOrderAmounts();
  };

  const handleHavingErrorValued = async (errors: ValidationErrors) => {
    if (order.order_type !== 'delivery') {
      if (errors['quote_id'] === 'noQuote') {
        delete errors['quote_id'];
      }
      setHavingError(!isEmpty(errors));
    } else {
      setHavingError(!isEmpty(errors));
    }
  };

  const orderInfo = (
    <OrderInfo
      key="orderInfo"
      config={{
        auto_move_send: store.auto_move_send,
        delay_delivery_time: store.delay_delivery_time,
        delay_pickup_time: store.delay_pickup_time,
        delay_dinein_time: store.delay_dinein_time,
        bizHours: store.biz_hours,
        holidays: store.holidays,
        deliverySettings: store.delivery_settings,
        acceptedOrderTypes: acceptOrderTypes
      }}
      onUpdateCalculatedValues={() => handleUpdateCalculatedValues()}
      onErrorsChange={errors => handleHavingErrorValued(errors)}
      isLandingPage={true}
      variant={variant}
    />
  );
  return useObserver(() => {
    return (
      <Dialog
        open
        onClose={onClose}
        fullWidth={variant === LayoutVariant.laptop}
        maxWidth="md"
        fullScreen={variant === LayoutVariant.mobile}
        scroll="paper"
      >
        <DialogTitle disableTypography>
          <Typography variant="h6" className={classes.title}>
            {order.order_type === 'delivery'
              ? 'Please input your address and select delivery time'
              : order.order_type === 'pickup'
              ? 'Please select your pick up time'
              : 'Please select your dine-in time'}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={onClose}
            className={classes.close}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers
          classes={{
            root:
              variant === LayoutVariant.mobile
                ? classes.mobileDialogContent
                : classes.dialogContent
          }}
        >
          {orderInfo}
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            disabled={
              havingError ||
              orderingStore.submitState === ViewState.LOADING ||
              orderingStore.deliveryQuoteState === ViewState.LOADING
            }
            onClick={onClose}
            color="primary"
          >
            Next
          </Button>
        </DialogActions>
      </Dialog>
    );
  });
};

export default OrderInfoLandingPage;
